import { useState, useEffect, useRef } from "react";
import { X, Send, MessageCircle, Minimize2, Maximize2, Sparkles } from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const SANIA_LOGO = "https://customer-assets.emergentagent.com/job_telecenter-hub/artifacts/dlcoh4rw_ChatGPT%20Image%206%20fe%CC%81vr.%202026%2C%2022_55_27.png";

export default function SanIAChat({ poleId, poleName, directorName, directorAvatar, userName = "Collaborateur" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Charger l'historique des messages
  useEffect(() => {
    if (isOpen && poleId) {
      loadMessages();
    }
  }, [isOpen, poleId]);

  // Scroll vers le bas quand nouveaux messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus sur l'input quand le chat s'ouvre
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const loadMessages = async () => {
    try {
      const resp = await authFetch(`${API}/api/chat/${poleId}/messages?limit=50`);
      if (resp.ok) {
        const data = await resp.json();
        setMessages(data.messages || []);
        setUnreadCount(0);
      }
    } catch (e) {
      console.error("Erreur chargement messages:", e);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: "user",
      content: inputValue.trim(),
      timestamp: new Date().toISOString(),
      sender_name: userName
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const resp = await authFetch(`${API}/api/chat/${poleId}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: userMessage.content,
          sender_name: userName
        })
      });

      if (resp.ok) {
        const data = await resp.json();
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          role: "assistant",
          content: data.response,
          timestamp: new Date().toISOString(),
          sender_name: directorName
        }]);
      } else {
        // Message d'erreur
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          role: "assistant",
          content: "Désolé, je rencontre un problème technique. Réessayez dans quelques instants.",
          timestamp: new Date().toISOString(),
          sender_name: directorName,
          isError: true
        }]);
      }
    } catch (e) {
      console.error("Erreur envoi message:", e);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: "assistant",
        content: "Erreur de connexion. Vérifiez votre connexion internet.",
        timestamp: new Date().toISOString(),
        sender_name: directorName,
        isError: true
      }]);
    }

    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  // Bouton flottant
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-6 z-50 group"
        data-testid="sania-chat-button"
      >
        <div className="relative">
          {/* Cercle lumineux animé */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-lg opacity-50 group-hover:opacity-75 animate-pulse"></div>
          
          {/* Bouton principal */}
          <div className="relative w-16 h-16 bg-gradient-to-br from-slate-800 to-slate-900 rounded-full border-2 border-blue-500/50 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
            <img 
              src={SANIA_LOGO} 
              alt="SAN-IA" 
              className="w-10 h-10 rounded-full object-cover"
            />
          </div>
          
          {/* Badge non-lu */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
          
          {/* Tooltip */}
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-slate-800 rounded-lg text-sm text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
            <span className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-400" />
              Discuter avec {directorName || "SAN-IA"}
            </span>
          </div>
        </div>
      </button>
    );
  }

  // Fenêtre de chat
  return (
    <div 
      className={`fixed z-50 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
        isMinimized 
          ? "bottom-24 right-6 w-72 h-14" 
          : "bottom-24 right-6 w-96 h-[600px] max-h-[80vh]"
      }`}
      data-testid="sania-chat-window"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img 
              src={SANIA_LOGO} 
              alt="SAN-IA" 
              className="w-10 h-10 rounded-full object-cover border-2 border-blue-500/50"
            />
            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-900"></span>
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm flex items-center gap-2">
              {directorAvatar && <span>{directorAvatar}</span>}
              {directorName || "SAN-IA"}
            </h3>
            <p className="text-xs text-slate-400">{poleName || "Assistant IA"}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <button 
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            {isMinimized ? <Maximize2 className="w-4 h-4 text-slate-400" /> : <Minimize2 className="w-4 h-4 text-slate-400" />}
          </button>
          <button 
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 h-[calc(100%-140px)]">
            {messages.length === 0 && (
              <div className="text-center py-10">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center">
                  <img src={SANIA_LOGO} alt="SAN-IA" className="w-12 h-12 rounded-full" />
                </div>
                <h4 className="font-semibold text-white mb-2">Bonjour ! 👋</h4>
                <p className="text-sm text-slate-400 max-w-xs mx-auto">
                  Je suis {directorName}, votre responsable IA pour le pôle {poleName}. Comment puis-je vous aider ?
                </p>
              </div>
            )}
            
            {messages.map((msg) => (
              <div 
                key={msg.id} 
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[80%] ${msg.role === "user" ? "order-2" : "order-1"}`}>
                  {msg.role === "assistant" && (
                    <div className="flex items-center gap-2 mb-1">
                      <img src={SANIA_LOGO} alt="" className="w-5 h-5 rounded-full" />
                      <span className="text-xs text-slate-500">{msg.sender_name}</span>
                    </div>
                  )}
                  <div 
                    className={`rounded-2xl px-4 py-2.5 ${
                      msg.role === "user" 
                        ? "bg-blue-600 text-white rounded-br-md" 
                        : msg.isError 
                          ? "bg-red-500/20 text-red-300 border border-red-500/30 rounded-bl-md"
                          : "bg-slate-800 text-slate-200 rounded-bl-md"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  <p className={`text-xs text-slate-500 mt-1 ${msg.role === "user" ? "text-right" : "text-left"}`}>
                    {formatTime(msg.timestamp)}
                  </p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-800 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-slate-700 p-4">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Écrivez votre message..."
                className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-blue-500 transition-colors"
                rows={1}
                style={{ maxHeight: "100px" }}
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="p-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors"
              >
                <Send className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
